import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import { PhotoLibrary as PhotosIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

// Migración de fotos de catálogo, Drive -> Cloud Storage (CLAUDE.md §7) - una
// sola biblioteca compartida entre países, emparejada por código de
// producto. Antes vivía como una pestaña dentro de "Google Sheets"; ahora
// es su propia página de menú.
export const PhotosMigration: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [photosFolderId, setPhotosFolderId] = useState('');
  const [photosUpload, setPhotosUpload] = useState(true);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photosResult, setPhotosResult] = useState<any | null>(null);

  const handleImportPhotos = async () => {
    try {
      setPhotosLoading(true);
      setPhotosResult(null);
      const result = await apiService.catalogPhotos.import(photosFolderId || undefined, photosUpload);
      setPhotosResult(result);
    } catch (error: any) {
      setPhotosResult({ error: error.response?.data?.detail || 'Error al importar fotos' });
    } finally {
      setPhotosLoading(false);
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <Box p={3}>
        <Alert severity="error">
          No tenés permisos para acceder a esta sección. Solo los administradores pueden migrar fotos.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" component="h1" gutterBottom>
        Fotos
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Migración de fotos del catálogo desde Google Drive hacia Cloud Storage. No es por país -
        ambos países comparten la misma carpeta de Drive y el mismo almacenamiento, emparejando
        por código de producto (CLAUDE.md §7).
      </Typography>

      <Card>
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="ID de carpeta de Drive (opcional)"
              value={photosFolderId}
              onChange={(e) => setPhotosFolderId(e.target.value)}
              fullWidth
              placeholder="Si se omite, usa la carpeta compartida configurada"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={photosUpload}
                  onChange={(e) => setPhotosUpload(e.target.checked)}
                />
              }
              label={photosUpload ? 'Subir fotos emparejadas' : 'Solo verificar (sin subir ni modificar nada)'}
            />
            <Box>
              <Button
                variant="contained"
                startIcon={photosLoading ? <CircularProgress size={20} /> : <PhotosIcon />}
                onClick={handleImportPhotos}
                disabled={photosLoading}
              >
                {photosUpload ? 'Migrar Fotos' : 'Verificar Conciliación'}
              </Button>
            </Box>

            {photosResult && (
              <Alert severity={photosResult.error ? 'error' : (photosResult.upload_errors?.length || photosResult.ambiguous?.length ? 'warning' : 'success')}>
                {photosResult.error ? (
                  photosResult.error
                ) : (
                  <Box>
                    <Typography variant="body2">
                      Fotos encontradas: {photosResult.photos_found} · Emparejadas: {photosResult.matched}
                      {photosUpload && ` · Subidas: ${photosResult.uploaded}`}
                    </Typography>
                    {photosResult.unmatched_skus?.length > 0 && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Sin foto ({photosResult.unmatched_skus.length}): {photosResult.unmatched_skus.slice(0, 10).join(', ')}
                        {photosResult.unmatched_skus.length > 10 && '…'}
                      </Typography>
                    )}
                    {photosResult.unmatched_photos?.length > 0 && (
                      <Typography variant="caption" display="block">
                        Fotos sin producto ({photosResult.unmatched_photos.length}): {photosResult.unmatched_photos.slice(0, 10).join(', ')}
                        {photosResult.unmatched_photos.length > 10 && '…'}
                      </Typography>
                    )}
                    {photosResult.ambiguous?.length > 0 && (
                      <Typography variant="caption" display="block" color="warning.main">
                        Ambiguas ({photosResult.ambiguous.length}): revisar manualmente
                      </Typography>
                    )}
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PhotosMigration;
